import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { sessionMonitor } from '../session/sessionMonitor';

const INSTALLED_FLAG = '__enaraAuthInterceptorInstalled';

/**
 * Report auth-shaped HTTP failures to the session monitor.
 *
 * Deliberately a *response error* interceptor that re-rejects the original
 * error untouched. That matters for two reasons:
 *
 *   - No call site has to change. The ~60 service catch blocks that rewrite the
 *     status to a hardcoded `500` still do so, but the interceptor already saw
 *     the real `AxiosError` first — an interceptor runs before the caller's
 *     `catch`. So detection does not depend on cleaning those up.
 *   - Swallowing or transforming the error here would silently change behaviour
 *     across the entire app.
 *
 * Idempotent, so double-installing on the same instance is harmless.
 */
export const installAuthInterceptor = (instance: AxiosInstance): AxiosInstance => {
  const flagged = instance as AxiosInstance & { [INSTALLED_FLAG]?: boolean };
  if (flagged[INSTALLED_FLAG]) {
    return instance;
  }
  flagged[INSTALLED_FLAG] = true;

  instance.interceptors.request.use((config) => sessionMonitor.tagRequest(config));
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      try {
        sessionMonitor.reportSuspiciousFailure(error);
      } catch (monitorError) {
        // A bug in the monitor must never break HTTP for the whole app.
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

/** `axios.create` with the auth interceptor already attached. */
export const createApiClient = (config: AxiosRequestConfig): AxiosInstance =>
  installAuthInterceptor(axios.create(config));

export { API_HOSTS, resolveApiHost } from './apiHosts';
export type { ApiHostKey } from './apiHosts';
